import { useState } from "react";
import { Modal, Button, Select, Row, Col, Badge, Radio } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import filtericon from "assets/account-filter.svg";
import styled from "@emotion/styled";
import dayjs from "dayjs";
import DatePicker from "antd/lib/date-picker";

const { Option } = Select;
const { RangePicker } = DatePicker;

const FilterModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 12px;
    padding: 0;
    overflow: hidden;
  }
  .ant-modal-header {
    padding: 18px 24px 14px;
    border-bottom: 1px solid #f0f0f0;
    margin: 0;
  }
  .ant-modal-title {
    font-size: 15px;
    font-weight: 600;
    color: #111;
  }
  .ant-modal-close {
    top: 14px;
    right: 16px;
  }
  .ant-modal-body {
    padding: 20px 24px 10px;
  }
  .ant-modal-footer {
    padding: 12px 24px 18px;
    border-top: 1px solid #f0f0f0;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin: 0;
  }
`;

const FieldLabel = styled.label`
  font-size: 12px;
  font-weight: 500;
  color: #555;
  display: block;
  margin-bottom: 6px;
`;

const StyledSelect = styled(Select)`
  width: 100%;
  .ant-select-selector {
    border-radius: 20px !important;
    min-height: 36px !important;
    height: auto !important;
    align-items: center;
    display: flex !important;
    font-size: 14px;
    flex-wrap: wrap;
    padding: 2px 8px !important;
  }
  &.ant-select-focused .ant-select-selector,
  &:hover .ant-select-selector {
    border-color: #e2e2e8 !important;
    box-shadow: 0 0 0 2px rgba(82, 198, 176, 0.15) !important;
  }
`;

const StyledRangePicker = styled(RangePicker)`
  width: 100%;
  border-radius: 20px !important;
  height: 36px;
  font-size: 14px;
  .ant-picker-input > input {
    font-size: 14px;
  }
  .ant-picker-range-separator {
    padding: 0 8px;
    color: #999;
  }
  &:hover,
  &.ant-picker-focused {
    border-color: #e2e2e8 !important;
    box-shadow: 0 0 0 2px rgba(82, 198, 176, 0.15) !important;
  }
`;

const ClearBtn = styled(Button)`
  border-radius: 6px;
  height: 36px;
  font-size: 13px;
  border: 1.5px solid #000 !important;
  color: #000 !important;
  background: white;
  &:hover,
  &:focus {
    border-color: #000 !important;
    color: #000 !important;
  }
`;

const ApplyBtn = styled(Button)`
  border-radius: 6px;
  height: 36px;
  font-size: 13px;
  background-color: #00685F !important;
  border-color: #00685F;
  color: #FFFFFF;
  &:hover,
  &:focus {
    background-color: #00685F !important;
    border-color: #00685F !important;
    color: #FFFFFF !important;
  }
`;

const FilterIconBtn = styled(Button)`
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1.5px solid #deefee;
  background: #deefee;
  color: #000;
  padding: 0;
  &:hover,
  &:focus {
    border-color: #deefee !important;
    color: #00685F !important;
    background: #deefee !important;
  }
`;

const FieldGroup = styled.div`
  margin-bottom: 16px;
`;

const StyledRadioGroup = styled(Radio.Group)`
  display: flex;
  gap: 16px;
  margin-top: 4px;
  .ant-radio-checked .ant-radio-inner {
    border-color: #10b981;
    background: #10b981;
  }
`;

const TYPE_OPTIONS = [
  "Medical",
  "Travel",
  "Stationery",
  "Food",
  "Other",
  "Subcontract",
];
const CURRENCY_OPTIONS = [
  "USD",
  "AUD",
  "EUR",
  "JPY",
  "KES",
  "INR",
  "GBP",
  "MUR",
];
const TAX_TYPE_OPTIONS = ["VAT", "Non-VAT"];

const emptyFilters = {
  logDateFrom: null,
  logDateTo: null,
  createdDateFrom: null,
  createdDateTo: null,
  type: [],
  currency: [],
  taxtype: null,
};

const countActiveFilters = (filters) => {
  const { logDateFrom, logDateTo, createdDateFrom, createdDateTo, ...rest } =
    filters;
  const restCount = Object.values(rest).filter((v) =>
    Array.isArray(v) ? v.length > 0 : v !== null && v !== undefined && v !== ""
  ).length;
  const logDateCount = logDateFrom || logDateTo ? 1 : 0;
  const createdDateCount = createdDateFrom || createdDateTo ? 1 : 0;
  return restCount + logDateCount + createdDateCount;
};

const ClaimsFilterModal = ({ onApply, onClear, loading = false }) => {
  const [open, setOpen] = useState(false);
  const [filters, setFilters] = useState(emptyFilters);
  const [appliedFilters, setAppliedFilters] = useState(emptyFilters);

  const set = (key) => (val) =>
    setFilters((prev) => ({ ...prev, [key]: val ?? null }));

  const handleLogDateRange = (dates) => {
    const [from, to] = dates ?? [];
    setFilters((prev) => ({
      ...prev,
      logDateFrom: from?.isValid() ? from.startOf("day").toISOString() : null,
      logDateTo: to?.isValid() ? to.endOf("day").toISOString() : null,
    }));
  };

  const handleCreatedDateRange = (dates) => {
    const [from, to] = dates ?? [];
    setFilters((prev) => ({
      ...prev,
      createdDateFrom: from?.isValid()
        ? from.startOf("day").toISOString()
        : null,
      createdDateTo: to?.isValid() ? to.endOf("day").toISOString() : null,
    }));
  };

  const handleClear = () => {
    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    onClear?.();
  };

  const handleApply = () => {
    setAppliedFilters(filters);
    onApply?.(filters);
    setOpen(false);
  };

  const handleCancel = () => {
    setFilters(appliedFilters);
    setOpen(false);
  };

  const badgeCount = countActiveFilters(appliedFilters);
  const liveCount = countActiveFilters(filters);

  return (
    <>
      <Badge count={badgeCount} size="small" color="red" offset={[-2, 2]}>
        <FilterIconBtn
          icon={
            <img
              src={filtericon}
              alt="Filter"
              style={{
                width: 18,
                height: 18,
                color: "#00685F",
              }}
            />
          }
          onClick={() => setOpen(true)}
        />
      </Badge>

      <FilterModal
        open={open}
        onCancel={handleCancel}
        title={`Filter${liveCount > 0 ? ` (${liveCount})` : ""}`}
        width={800}
        centered
        destroyOnClose={false}
        closeIcon={<CloseOutlined style={{ fontSize: 13 }} />}
        footer={[
          <ClearBtn key="clear" onClick={handleClear}>
            Clear All
          </ClearBtn>,
          <ApplyBtn
            key="apply"
            type="primary"
            onClick={handleApply}
            loading={loading}
          >
            Apply Filter
          </ApplyBtn>,
        ]}
      >
        <Row gutter={[16, 0]}>
          {/* Log Date Range */}
          <Col span={12}>
            <FieldGroup>
              <FieldLabel>Log Date Range</FieldLabel>
              <StyledRangePicker
                placeholder={["From", "To"]}
                format="DD-MM-YYYY"
                value={
                  filters.logDateFrom && filters.logDateTo
                    ? [dayjs(filters.logDateFrom), dayjs(filters.logDateTo)]
                    : undefined
                }
                onChange={handleLogDateRange}
                separator={
                  <span style={{ color: "#999", fontSize: 14 }}>↔</span>
                }
              />
            </FieldGroup>
          </Col>

          {/* Created Date Range */}
          <Col span={12}>
            <FieldGroup>
              <FieldLabel>Created Date Range</FieldLabel>
              <StyledRangePicker
                placeholder={["From", "To"]}
                format="DD-MM-YYYY"
                value={
                  filters.createdDateFrom && filters.createdDateTo
                    ? [
                      dayjs(filters.createdDateFrom),
                      dayjs(filters.createdDateTo),
                    ]
                    : undefined
                }
                onChange={handleCreatedDateRange}
                separator={
                  <span style={{ color: "#999", fontSize: 14 }}>↔</span>
                }
              />
            </FieldGroup>
          </Col>

          {/* Type */}
          <Col span={8}>
            <FieldGroup>
              <FieldLabel>Type</FieldLabel>
              <StyledSelect
                mode="multiple"
                placeholder="All"
                value={filters.type?.length ? filters.type : undefined}
                onChange={set("type")}
                allowClear
              >
                {TYPE_OPTIONS.map((t) => (
                  <Option key={t} value={t}>
                    {t}
                  </Option>
                ))}
              </StyledSelect>
            </FieldGroup>
          </Col>

          {/* Currency */}
          <Col span={8}>
            <FieldGroup>
              <FieldLabel>Currency</FieldLabel>
              <StyledSelect
                mode="multiple"
                placeholder="All"
                value={filters.currency?.length ? filters.currency : undefined}
                onChange={set("currency")}
                allowClear
              >
                {CURRENCY_OPTIONS.map((c) => (
                  <Option key={c} value={c}>
                    {c}
                  </Option>
                ))}
              </StyledSelect>
            </FieldGroup>
          </Col>

          {/* Tax Type */}
          <Col span={8}>
            <FieldGroup>
              <FieldLabel>Tax Type</FieldLabel>
              <StyledRadioGroup
                value={filters.taxtype}
                onChange={(e) => set("taxtype")(e.target.value)}
              >
                {TAX_TYPE_OPTIONS.map((t) => (
                  <Radio key={t} value={t}>
                    {t}
                  </Radio>
                ))}
              </StyledRadioGroup>
            </FieldGroup>
          </Col>
        </Row>
      </FilterModal>
    </>
  );
};

export default ClaimsFilterModal;
